import { zipSync, strToU8 } from 'fflate';
import { PrintJob, PrintPartKind } from './types';
import { encodeBinaryStl } from './stl';

export function partFileName(part: { id: string; kind: string }): string {
  return `${part.id}.stl`;
}

function partFolder(kind: PrintPartKind): string {
  switch (kind) {
    case 'track':
      return 'track';
    case 'support':
      return 'supports';
    case 'ground':
      return 'ground';
    case 'connector':
      return 'connectors';
    default: {
      const _never: never = kind;
      return _never;
    }
  }
}

/** Build a ZIP of all print parts plus a small manifest. */
export function buildPrintZip(job: PrintJob, coasterName: string): Uint8Array {
  const files: Record<string, Uint8Array> = {};
  const manifest = {
    name: coasterName,
    chunkSizeMm: job.settings.chunkSizeMm,
    separateSupport: job.settings.separateSupport,
    splitAtToothBottom: job.settings.splitAtToothBottom,
    groundPlaneEnabled: job.settings.groundPlaneEnabled,
    groundPlaneHeightMm: job.settings.groundPlaneHeightMm,
    groundButterflyDepthMm: job.settings.groundButterflyDepthMm,
    printScale: '1:100',
    cuts: job.cuts,
    warnings: job.warnings,
    parts: job.parts.map((p) => ({
      id: p.id,
      kind: p.kind,
      s0: p.s0,
      s1: p.s1,
      obbMm: p.obbMm,
      oversized: p.oversized,
      fixed: p.fixed,
      fixWarning: p.fixWarning,
      file: `${partFolder(p.kind)}/${partFileName(p)}`,
    })),
  };
  files['manifest.json'] = strToU8(JSON.stringify(manifest, null, 2));
  files['print-log.txt'] = strToU8((job.log ?? []).join('\n') + '\n');
  files['README.txt'] = strToU8(
    [
      `RCSimFable print export: ${coasterName}`,
      `Scale: 1:100 (coordinates in STL are millimetres)`,
      `Build cube: ${job.settings.chunkSizeMm} mm`,
      `SeparateSupport: ${job.settings.separateSupport}`,
      `Split at nearest tooth bottom: ${job.settings.splitAtToothBottom}`,
      `Ground plane: ${job.settings.groundPlaneEnabled ? `on (${job.settings.groundPlaneHeightMm} mm slab, ${job.settings.groundButterflyDepthMm} mm butterfly pocket)` : 'off'}`,
      '',
      'Prototype meshes solidified with Manifold boolean union (STL fixer) for slicer-ready solids.',
      'Track joints: both chunk faces are female sockets. Print connector-pin.stl once and reuse it at every joint (0.25 mm clearance at print scale).',
      'Support beam splices: both cut faces are female sockets. Print support-connector-pin.stl once and reuse it at every splice (0.25 mm clearance at print scale), plus HSS bolted flanges when that setting is on.',
      'Ground plane: tiles cover the coaster footprint. Square clips on top match footer pad size/thickness so printed footers drop in. Tile seams have a butterfly / bow-tie key pocket — print ground-butterfly-key.stl once and reuse it.',
      `Parts solidified: ${job.parts.filter((p) => p.fixed).length}/${job.parts.length}`,
      '',
      ...job.warnings.map((w) => `Warning: ${w}`),
      '',
      'See print-log.txt for full generation diagnostics.',
    ].join('\n'),
  );

  for (const part of job.parts) {
    files[`${partFolder(part.kind)}/${partFileName(part)}`] = encodeBinaryStl(part.mesh, part.id);
  }

  return zipSync(files, { level: 6 });
}
